import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ImageViewer } from '~/components/common/image-viewer/image-viewer';
import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

import { pickFullUrl, pickThumbUrl } from '../utils/image-urls';

import { LazyImage } from './lazy-image';

/** Thumbnail sizes used across catalog-enrichment: dense grids, tables, card rows, page headers. */
const SIZE_CLASS = {
  xs: 'size-8',
  sm: 'size-10',
  md: 'size-14',
  lg: 'size-18',
} as const;

/** The same sizes in CSS px — the `<img>` width/height, so the browser reserves the box. */
const SIZE_PX: Record<keyof typeof SIZE_CLASS, number> = { xs: 32, sm: 40, md: 56, lg: 72 };

export type ProductThumbSize = keyof typeof SIZE_CLASS;

interface Props {
  /** Image URL; null/undefined/broken renders the shared placeholder in the same box. */
  src: string | null | undefined;
  /**
   * The full-size image the enlarged viewer opens in place of `src` — pass it when `src` is a
   * small rendition (the 160px `thumbnailUrl`) so the viewer never shows a blurry upscale.
   */
  fullSrc?: string | null | undefined;
  /** Product title — used as alt text and as the viewer title. */
  title: string;
  size?: ProductThumbSize | undefined;
  /** Extra images for the enlarged viewer (e.g. additional_image_urls); `src` is shown first. */
  gallery?: (string | null | undefined)[] | undefined;
  className?: string | undefined;
  /** Display size in px when `className` resizes the box (e.g. a card-wide image); else from `size`. */
  pixelSize?: number | undefined;
}

/**
 * The one product thumbnail every catalog-enrichment list and header uses: fixed square box
 * (no layout shift while loading or when an image is missing), loaded only when near the
 * viewport via `LazyImage`, and click-to-enlarge in the shared `ImageViewer` when an image exists.
 */
export function ProductThumb({
  src,
  fullSrc,
  title,
  size = 'sm',
  gallery,
  className,
  pixelSize,
}: Props) {
  const { t } = useTranslation('catalog-enrichment');
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  // The box draws the (possibly smaller) thumb; the viewer opens the full image first. An empty
  // string is "no image", like null.
  const thumb = pickThumbUrl({ thumbnailUrl: src, optimizedUrl: fullSrc });
  const images = [
    pickFullUrl({ optimizedUrl: fullSrc, sourceUrl: src }),
    ...(gallery ?? []),
  ].filter((u): u is string => !!u);
  const unique = [...new Set(images)];
  const px = pixelSize ?? SIZE_PX[size];
  const box = cn(
    'bg-muted shrink-0 overflow-hidden rounded-md border',
    SIZE_CLASS[size],
    className,
  );

  if (unique.length === 0) {
    return (
      <div className={box} role="img" aria-label={t('thumb.noImage', { title })}>
        <LazyImage src={null} alt="" width={px} height={px} />
      </div>
    );
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        aria-label={t('thumb.enlarge', { title })}
        className={cn(box, 'h-auto cursor-pointer p-0 hover:opacity-90')}
        onClick={(e) => {
          // A thumbnail inside a clickable row opens the image, not the row.
          e.stopPropagation();
          setIndex(0);
          setOpen(true);
        }}
      >
        <LazyImage src={thumb ?? unique[0]} alt={title} width={px} height={px} />
      </Button>
      <ImageViewer
        images={unique}
        index={index}
        onIndexChange={setIndex}
        open={open}
        onOpenChange={setOpen}
        title={title}
      />
    </>
  );
}
